import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'

import TimesheetButtons from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimesheetButtons'
import CounterWidget from 'modules/attendance/components/ClockIn/Shifts/DailyShifts/DailyCounter/CounterWidget'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { useHalfDayShifts } from 'modules/attendance/hooks/shifts/useHalfDayShifts'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { trackDailyClockIn } from 'modules/attendance/lib/tracking'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'

import DailyCounter from './DailyCounter'
import DailyTable from './DailyTable'

interface Props {
  store: Store
  showHourBalance: boolean
}

const DailyShifts = ({ store, showHourBalance }: Props) => {
  const { deleteShift, createShift } = useShiftActions()
  const { currentMonth } = store

  const { shifts, reviewsStats, attendancePeriods, featureByName, loading } = useTimesheetData()

  const {
    stats,
    loading: loadingHalfDayShift,
    refetch,
    submitBulkDailyShiftChange,
  } = useHalfDayShifts({
    year: store.year,
    employeeId: store.employeeId,
    currentMonth,
  })

  const submitHalfDayShift = async (shift: TimesheetPageShiftFragment) => {
    if (!AttendanceShiftUtils.isNew(shift)) {
      const { success } = await asyncAction(
        () =>
          deleteShift({
            id: shift.id,
            date: shift.referenceDate,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        refetch()
      }
    } else {
      const { success } = await asyncAction(
        () =>
          createShift({
            date: shift.date,
            referenceDate: shift.date,
            halfDay: shift.halfDay,
            employeeId: store.employeeId,
            workable: true,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        refetch()
        trackDailyClockIn('timesheet_daily_desktop')
      }
    }
  }

  const { dateFrom, dateTo } = useAttendanceDateNavigator()
  const rangeDays = AttendanceShiftUtils.daysBetweenDates(moment(dateFrom), moment(dateTo))
  const FJffenabled = TimesheetFeatures.FJ_WIDGET in featureByName
  const year = currentMonth.year()

  return (
    <>
      <Box paddingBottom="s24">
        {FJffenabled ? (
          <CounterWidget loading={loading || loadingHalfDayShift} year={year} {...stats} />
        ) : (
          <DailyCounter year={year} {...stats} />
        )}
      </Box>

      <TimesheetButtons
        employeeId={store.employeeId}
        showHourBalance={showHourBalance}
        isDaily
        submitBulkDailyShiftChange={submitBulkDailyShiftChange}
      />

      <DailyTable
        store={store}
        submitHalfDayShift={submitHalfDayShift}
        shiftsHash={AttendanceShiftUtils.halfDayShifts({
          attendancePeriods,
          employeeId: store.employeeId,
          shifts,
          rangeDays,
        })}
        rangeDays={rangeDays}
        reviewsStats={reviewsStats}
      />
    </>
  )
}

export default DailyShifts
